import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { loadCurrentUser, setCredentials } from "../../store/authSlice";
import { NAV } from "../Navbar/navbarTheme";
import { extractAuthToken, setAuthToken, signInWithCredentials } from "../utils/authApi";
import {
  COUNTRY_CALLING_CODES,
  getCountryByIso,
  isoToFlagEmoji,
} from "../data/countryCallingCodes";
import AuthFormLayout from "./AuthFormLayout";
import {
  AUTH_PRIMARY,
  authAlertSx,
  authFieldSx,
  authFooterTextSx,
  authLinkSx,
  authSubmitButtonSx,
} from "./authStyles";

const REGISTER_API = "https://uktomars.co.uk/api/auth/register";

const LIMITS = {
  name: 60,
  company: 60,
  county: 60,
  email: 60,
  phoneLocal: 20,
};

const allowedSimple = /^[a-zA-Z0-9 ]*$/;

const RegisterPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [county, setCounty] = useState("");
  const [email, setEmail] = useState("");
  const [countryIso, setCountryIso] = useState("GB");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const selectedCountry = useMemo(() => {
    return (
      getCountryByIso(countryIso) ??
      getCountryByIso("GB") ?? {
        iso: "GB",
        name: "United Kingdom",
        dial: "+44",
      }
    );
  }, [countryIso]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const handleNameChange = (event) => {
    const value = event.target.value;
    if (value.length <= LIMITS.name && allowedSimple.test(value)) {
      setName(value);
    }
  };

  const handleCompanyChange = (event) => {
    const value = event.target.value;
    if (value.length <= LIMITS.company && allowedSimple.test(value)) {
      setCompany(value);
    }
  };

  const handleCountyChange = (event) => {
    const value = event.target.value;
    if (value.length <= LIMITS.county && allowedSimple.test(value)) {
      setCounty(value);
    }
  };

  const handlePhoneChange = (event) => {
    const value = event.target.value.replace(/\D/g, "");
    if (value.length <= LIMITS.phoneLocal) {
      setPhone(value);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");

    const trimmedEmail = email.trim();
    const trimmedName = name.trim();
    const trimmedCompany = company.trim();
    const trimmedCounty = county.trim();

    if (
      !trimmedName ||
      !trimmedCounty ||
      !trimmedEmail ||
      !phone ||
      !password
    ) {
      setError("Please complete all required fields.");
      return;
    }

    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    if (!acceptTerms) {
      setError("Please accept the Terms and Conditions to register.");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(REGISTER_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          name: trimmedName,
          email: trimmedEmail,
          password,
          password_confirmation: confirmPassword,
          country_code: selectedCountry.dial,
          phone,
          company: trimmedCompany,
          county: trimmedCounty,
        }),
      });

      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        const validationMessage = payload?.errors
          ? Object.values(payload.errors).flat().join(" ")
          : null;
        throw new Error(
          validationMessage ||
            payload?.message ||
            "Unable to create your account. Please try again.",
        );
      }

      let token = extractAuthToken(payload);
      if (!token) {
        token = await signInWithCredentials({
          email: trimmedEmail,
          password,
        });
      } else {
        setAuthToken(token);
      }

      dispatch(setCredentials({ token }));
      await dispatch(loadCurrentUser()).unwrap();

      const redirectTo =
        typeof location.state?.from === "string" && location.state.from
          ? location.state.from
          : "/Profile";
      navigate(redirectTo, { replace: true });
    } catch (submitError) {
      setError(
        submitError?.message ||
          "Unable to register right now. Please try again later.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AuthFormLayout
      wide
      title="Register"
      subtitle="Create a trade account for wholesale pricing, quotes, and export enquiries."
      icon={PersonAddAltOutlinedIcon}
      footer={
        <Typography sx={authFooterTextSx}>
          Already have an account?{" "}
          <Typography
            component={Link}
            to="/SignIn"
            sx={{ ...authLinkSx, display: "inline" }}
          >
            Sign in
          </Typography>
        </Typography>
      }
    >
      {error ? (
        <Alert severity="error" sx={authAlertSx}>
          {error}
        </Alert>
      ) : null}

      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Stack spacing={2}>
          <TextField
            fullWidth
            required
            label="Full name"
            value={name}
            onChange={handleNameChange}
            sx={authFieldSx}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonOutlineIcon
                    sx={{ color: NAV.inkMuted, fontSize: 20 }}
                  />
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            label="Company name (optional)"
            value={company}
            onChange={handleCompanyChange}
            sx={authFieldSx}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <BusinessOutlinedIcon
                    sx={{ color: NAV.inkMuted, fontSize: 20 }}
                  />
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            required
            label="Email address"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            sx={authFieldSx}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailOutlinedIcon
                    sx={{ color: NAV.inkMuted, fontSize: 20 }}
                  />
                </InputAdornment>
              ),
            }}
          />

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <FormControl fullWidth size="small" sx={authFieldSx}>
              <InputLabel id="register-country-label">Country code</InputLabel>
              <Select
                labelId="register-country-label"
                value={countryIso}
                label="Country code"
                onChange={(event) => setCountryIso(event.target.value)}
              >
                {COUNTRY_CALLING_CODES.map((country) => (
                  <MenuItem key={country.iso} value={country.iso}>
                    {isoToFlagEmoji(country.iso)} {country.name} ({country.dial}
                    )
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              required
              label="Phone number"
              value={phone}
              onChange={handlePhoneChange}
              sx={authFieldSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneOutlinedIcon
                      sx={{ color: NAV.inkMuted, fontSize: 20 }}
                    />
                    <Typography
                      sx={{
                        ml: 0.5,
                        fontSize: "0.875rem",
                        color: NAV.inkMuted,
                        fontWeight: 600,
                      }}
                    >
                      {selectedCountry.dial}
                    </Typography>
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
          <TextField
            fullWidth
            required
            label="County"
            value={county}
            onChange={handleCountyChange}
            sx={authFieldSx}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LocationOnOutlinedIcon
                    sx={{ color: NAV.inkMuted, fontSize: 20 }}
                  />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            fullWidth
            required
            label="Password"
            type={showPassword ? "text" : "password"}
            autoComplete="new-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            helperText="At least 8 characters"
            sx={authFieldSx}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlinedIcon
                    sx={{ color: NAV.inkMuted, fontSize: 20 }}
                  />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <Button
                    type="button"
                    size="small"
                    onClick={() => setShowPassword((open) => !open)}
                    sx={{
                      minWidth: "auto",
                      px: 0.75,
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color: NAV.inkMuted,
                    }}
                  >
                    {showPassword ? "Hide" : "Show"}
                  </Button>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            required
            label="Confirm password"
            type={showConfirmPassword ? "text" : "password"}
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
            sx={authFieldSx}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlinedIcon
                    sx={{ color: NAV.inkMuted, fontSize: 20 }}
                  />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <Button
                    type="button"
                    size="small"
                    onClick={() => setShowConfirmPassword((open) => !open)}
                    sx={{
                      minWidth: "auto",
                      px: 0.75,
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color: NAV.inkMuted,
                    }}
                  >
                    {showConfirmPassword ? "Hide" : "Show"}
                  </Button>
                </InputAdornment>
              ),
            }}
          />

          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={acceptTerms}
                onChange={(event) => setAcceptTerms(event.target.checked)}
                sx={{
                  color: NAV.inkMuted,
                  "&.Mui-checked": { color: AUTH_PRIMARY },
                }}
              />
            }
            label={
              <Typography sx={{ fontSize: "0.875rem", color: NAV.ink }}>
                I have read and agree to the{" "}
                <Typography
                  component={Link}
                  to="/TermsAndConditions"
                  sx={{ ...authLinkSx, display: "inline", fontSize: "inherit" }}
                >
                  Terms and Conditions
                </Typography>{" "}
                and the{" "}
                <Typography
                  component={Link}
                  to="/PrivacyPolicy"
                  sx={{ ...authLinkSx, display: "inline", fontSize: "inherit" }}
                >
                  Privacy Policy
                </Typography>
              </Typography>
            }
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={isSubmitting}
            sx={authSubmitButtonSx}
          >
            {isSubmitting ? "Creating account..." : "Create account"}
          </Button>
        </Stack>
      </Box>
    </AuthFormLayout>
  );
};

export default RegisterPage;
